import React, { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "../services/apiService";
import forgotOffice from "../assets/img/forgot-password-office.jpeg";
import forgotOfficeDark from "../assets/img/forgot-password-office-dark.jpeg";

const STEPS = { IDENTIFIER: 1, OTP: 2, NEW_PASSWORD: 3, DONE: 4 };

// ── Static sub-components defined OUTSIDE the parent so React never remounts them ──

const StepIndicator = ({ step }) => {
  const labels = ["Account", "OTP", "Password"];
  const currentIdx = step - 1;
  return (
    <div className="flex items-center mb-6">
      {labels.map((label, idx) => (
        <React.Fragment key={label}>
          <div className="flex flex-col items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all ${
                idx < currentIdx
                  ? "bg-emerald-500 border-emerald-500 text-white"
                  : idx === currentIdx
                  ? "border-emerald-400 text-emerald-400"
                  : "border-gray-600 text-gray-600"
              }`}
            >
              {idx < currentIdx ? "✓" : idx + 1}
            </div>
            <span className={`text-xs mt-1 ${idx <= currentIdx ? "text-emerald-400" : "text-gray-600"}`}>
              {label}
            </span>
          </div>
          {idx < labels.length - 1 && (
            <div
              className={`flex-1 h-0.5 mx-2 mb-5 transition-all ${
                idx < currentIdx ? "bg-emerald-500" : "bg-gray-700"
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const ErrorBanner = ({ error }) =>
  error ? (
    <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded-lg text-red-300 text-sm">
      {error}
    </div>
  ) : null;

const Layout = ({ step, children }) => {
  const subtitle = {
    [STEPS.IDENTIFIER]: "Enter your registered email or phone",
    [STEPS.OTP]: "Enter the OTP sent to your phone",
    [STEPS.NEW_PASSWORD]: "Create your new password",
    [STEPS.DONE]: "You're all set!",
  }[step];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 via-gray-800 to-gray-700 p-4">
      <div className="flex flex-col md:flex-row w-full max-w-4xl overflow-hidden rounded-xl shadow-2xl bg-gray-900 text-white">
        {/* Left image panel */}
        <div className="md:w-5/12 h-52 md:h-auto relative shrink-0">
          <img loading="lazy"
            src={forgotOffice}
            alt="Office"
            className="object-cover w-full h-full rounded-t-xl md:rounded-l-xl md:rounded-tr-none dark:hidden"
          />
          <img loading="lazy"
            src={forgotOfficeDark}
            alt="Office Dark"
            className="hidden object-cover w-full h-full rounded-t-xl md:rounded-l-xl md:rounded-tr-none dark:block"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent rounded-t-xl md:rounded-l-xl md:rounded-tr-none" />
          <div className="absolute bottom-5 left-5 right-5">
            <h2 className="text-2xl font-bold">Forgot Password?</h2>
            <p className="text-gray-300 mt-1 text-sm">{subtitle}</p>
          </div>
        </div>

        {/* Right form panel */}
        <main className="md:w-7/12 flex items-center justify-center p-6 md:p-10">
          <div className="w-full max-w-sm">{children}</div>
        </main>
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const ForgotPassword = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(STEPS.IDENTIFIER);
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [resetToken, setResetToken] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  const otpRefs = useRef([]);

  // ── Step 1: send OTP ────────────────────────────────────────────────────────
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError("");
    if (!identifier.trim()) {
      setError("Please enter your email or phone number.");
      return;
    }
    setLoading(true);
    try {
      const res = await authService.forgotPassword(identifier.trim());
      setResetToken(res.resetToken);
      setMaskedPhone(res.maskedPhone || "");
      if (res.devOtp) setDevOtp(res.devOtp);
      setStep(STEPS.OTP);
      startResendCooldown();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── OTP input helpers ───────────────────────────────────────────────────────
  const handleOtpChange = useCallback((index, value) => {
    if (!/^\d?$/.test(value)) return;
    setOtp((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    if (value && index < 5) {
      setTimeout(() => otpRefs.current[index + 1]?.focus(), 0);
    }
  }, []);

  const handleOtpKeyDown = useCallback((index, e) => {
    if (e.key === "Backspace") {
      if (otp[index]) {
        // Clear current box
        setOtp((prev) => { const n = [...prev]; n[index] = ""; return n; });
      } else if (index > 0) {
        // Move to previous box
        otpRefs.current[index - 1]?.focus();
        setOtp((prev) => { const n = [...prev]; n[index - 1] = ""; return n; });
      }
    }
  }, [otp]);

  const handleOtpPaste = useCallback((e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length > 0) {
      const next = ["", "", "", "", "", ""];
      pasted.split("").forEach((ch, i) => { next[i] = ch; });
      setOtp(next);
      const focusIdx = Math.min(pasted.length, 5);
      setTimeout(() => otpRefs.current[focusIdx]?.focus(), 0);
    }
  }, []);

  const enteredOtp = otp.join("");

  // ── Step 2: proceed to password ────────────────────────────────────────────
  const handleVerifyOtp = (e) => {
    e.preventDefault();
    setError("");
    if (enteredOtp.length !== 6) {
      setError("Please enter the complete 6-digit OTP.");
      return;
    }
    setStep(STEPS.NEW_PASSWORD);
  };

  // ── Resend cooldown ─────────────────────────────────────────────────────────
  const startResendCooldown = () => {
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError("");
    setLoading(true);
    try {
      const res = await authService.forgotPassword(identifier.trim());
      setResetToken(res.resetToken);
      if (res.devOtp) setDevOtp(res.devOtp);
      setOtp(["", "", "", "", "", ""]);
      setTimeout(() => otpRefs.current[0]?.focus(), 50);
      startResendCooldown();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3: reset password ──────────────────────────────────────────────────
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await authService.resetPassword(resetToken, enteredOtp, newPassword);
      setStep(STEPS.DONE);
    } catch (err) {
      setError(err.message);
      if (err.message.toLowerCase().includes("otp") || err.message.toLowerCase().includes("expired")) {
        setStep(STEPS.OTP);
        setOtp(["", "", "", "", "", ""]);
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Password strength ───────────────────────────────────────────────────────
  const getStrength = (pwd) => {
    if (!pwd) return { level: 0, label: "", color: "" };
    if (pwd.length < 6) return { level: 1, label: "Too short", color: "bg-red-500" };
    if (pwd.length < 9) return { level: 2, label: "Fair", color: "bg-yellow-500" };
    if (pwd.length < 12) return { level: 3, label: "Good", color: "bg-blue-400" };
    return { level: 4, label: "Strong", color: "bg-emerald-500" };
  };
  const strength = getStrength(newPassword);

  // ══════════════════════════════════════════════════════════════════════════
  // STEP 1 — Enter email / phone
  // ══════════════════════════════════════════════════════════════════════════
  if (step === STEPS.IDENTIFIER) {
    return (
      <Layout step={step}>
        <h1 className="mb-1 text-2xl font-bold text-emerald-400">Recover Password</h1>
        <p className="text-gray-400 text-sm mb-5">We'll send an OTP to your registered mobile number.</p>
        <StepIndicator step={step} />
        <ErrorBanner error={error} />
        <form onSubmit={handleSendOtp} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Email or Phone Number
            </label>
            <input
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="john@doe.com or 9876543210"
              autoComplete="username"
              autoFocus
              className="block w-full h-12 px-4 text-white text-base rounded-lg bg-gray-800 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 placeholder-gray-500 transition"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full h-12 text-sm font-semibold text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 active:bg-emerald-700 transition-all shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? "Sending OTP…" : "Send OTP"}
          </button>
        </form>
        <p className="mt-5 text-sm text-gray-400">
          Remember your password?{" "}
          <a href="/login" className="text-emerald-400 hover:underline">Login</a>
        </p>
      </Layout>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // STEP 2 — Enter OTP
  // ══════════════════════════════════════════════════════════════════════════
  if (step === STEPS.OTP) {
    return (
      <Layout step={step}>
        <h1 className="mb-1 text-2xl font-bold text-emerald-400">Enter OTP</h1>
        <p className="text-gray-400 text-sm mb-1">
          Sent to <span className="text-white font-medium">{maskedPhone || "your phone"}</span>
        </p>
        {devOtp && (
          <div className="text-yellow-400 text-xs mb-3 bg-yellow-900/30 border border-yellow-700 rounded px-3 py-2">
            Dev mode — OTP:{" "}
            <span className="font-bold tracking-[0.3em]">{devOtp}</span>
          </div>
        )}
        <StepIndicator step={step} />
        <ErrorBanner error={error} />
        <form onSubmit={handleVerifyOtp}>
          <label className="block text-sm font-medium text-gray-300 mb-3">6-digit OTP</label>

          {/* OTP boxes — full width, evenly spaced */}
          <div
            className="grid grid-cols-6 gap-2 mb-6"
            onPaste={handleOtpPaste}
          >
            {otp.map((digit, idx) => (
              <input
                key={idx}
                ref={(el) => (otpRefs.current[idx] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                autoFocus={idx === 0}
                onChange={(e) => handleOtpChange(idx, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                className={`w-full aspect-square text-center text-2xl font-bold text-white bg-gray-800 border-2 rounded-xl focus:outline-none transition select-none caret-transparent ${
                  digit
                    ? "border-emerald-400 bg-gray-700"
                    : "border-gray-600 focus:border-emerald-400"
                }`}
              />
            ))}
          </div>

          <button
            type="submit"
            disabled={enteredOtp.length !== 6}
            className="w-full h-12 text-sm font-semibold text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 active:bg-emerald-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Verify OTP
          </button>
        </form>

        <div className="mt-4 flex items-center justify-between text-sm">
          <button
            onClick={() => { setStep(STEPS.IDENTIFIER); setError(""); }}
            className="text-gray-500 hover:text-gray-300 transition"
          >
            ← Change email/phone
          </button>
          <button
            onClick={handleResend}
            disabled={resendCooldown > 0 || loading}
            className="text-emerald-400 hover:underline disabled:text-gray-600 disabled:no-underline disabled:cursor-not-allowed transition"
          >
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend OTP"}
          </button>
        </div>
      </Layout>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // STEP 3 — Set new password
  // ══════════════════════════════════════════════════════════════════════════
  if (step === STEPS.NEW_PASSWORD) {
    return (
      <Layout step={step}>
        <h1 className="mb-1 text-2xl font-bold text-emerald-400">New Password</h1>
        <p className="text-gray-400 text-sm mb-5">Choose a strong password (min. 6 characters).</p>
        <StepIndicator step={step} />
        <ErrorBanner error={error} />
        <form onSubmit={handleResetPassword} className="space-y-4">
          {/* New password */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">New Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 6 characters"
                autoComplete="new-password"
                autoFocus
                className="block w-full h-12 px-4 pr-16 text-white text-base rounded-lg bg-gray-800 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 placeholder-gray-500 transition"
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-emerald-400 hover:text-emerald-300 px-1 py-0.5 rounded transition"
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>

            {/* Strength bar */}
            {newPassword.length > 0 && (
              <div className="mt-2 space-y-1">
                <div className="flex gap-1">
                  {[1, 2, 3, 4].map((lvl) => (
                    <div
                      key={lvl}
                      className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                        strength.level >= lvl ? strength.color : "bg-gray-700"
                      }`}
                    />
                  ))}
                </div>
                <p className="text-xs text-gray-500">{strength.label}</p>
              </div>
            )}
          </div>

          {/* Confirm password */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Confirm Password</label>
            <div className="relative">
              <input
                type={showConfirm ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat new password"
                autoComplete="new-password"
                className={`block w-full h-12 px-4 pr-16 text-white text-base rounded-lg bg-gray-800 border focus:outline-none focus:ring-2 focus:ring-emerald-400 placeholder-gray-500 transition ${
                  confirmPassword && confirmPassword !== newPassword
                    ? "border-red-500 focus:border-red-500"
                    : "border-gray-700 focus:border-emerald-400"
                }`}
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-emerald-400 hover:text-emerald-300 px-1 py-0.5 rounded transition"
              >
                {showConfirm ? "Hide" : "Show"}
              </button>
            </div>
            {confirmPassword && confirmPassword !== newPassword && (
              <p className="text-red-400 text-xs mt-1">Passwords do not match</p>
            )}
            {confirmPassword && confirmPassword === newPassword && (
              <p className="text-emerald-400 text-xs mt-1">✓ Passwords match</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || newPassword.length < 6 || newPassword !== confirmPassword}
            className="w-full h-12 text-sm font-semibold text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 active:bg-emerald-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Resetting…" : "Reset Password"}
          </button>
        </form>
      </Layout>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // STEP 4 — Success
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <Layout step={step}>
      <div className="text-center py-4">
        <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-emerald-400 mb-3">Password Reset!</h1>
        <p className="text-gray-400 text-sm mb-8">
          Your password has been updated. You can now log in with your new password.
        </p>
        <button
          onClick={() => navigate("/login")}
          className="w-full h-12 text-sm font-semibold text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 transition-all shadow-lg"
        >
          Go to Login
        </button>
      </div>
    </Layout>
  );
};

export default ForgotPassword;
